import React, { useMemo } from 'react';
import classnames from 'classnames/bind';
import { identity, isEmpty, noop } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
    CloseIcon,
    pxToRem,
    STYLED_BUTTON_SIZE,
    STYLED_BUTTON_TYPE,
    StyledButton,
    DropdownMenu,
    DelimiterWrapper,
    AsyncDropdown,
    DashboardRangePicker,
    DASHBOARD_RANGE_PICKER_PRESETS as PRESETS,
} from 'taboola-ultimate-ui';
import { InputEditField } from 'modules/campaigns/modules/campaigns-reports/components/CellEditors/InputEditField';
import { RangePickerHeader } from 'modules/campaigns/modules/campaigns-reports/components/RangePickerHeader/RangePickerHeader';
import TimeFrame from 'modules/campaigns/modules/campaigns-reports/components/TimeFrame/TimeFrame';
import { DATE_PRESET, END_DATE, START_DATE, useDateRange } from 'modules/campaigns/modules/campaigns-reports/hooks';
import { renderWeekHeaderElement } from 'modules/campaigns/modules/campaigns-reports/utils';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFilterListValueState } from './useFilterListValueState';
import { useFilterOperatorState } from './useFilterOperatorState';
import { useFilterPlainValuesState } from './useFilterPlainValuesState';
import { useFilterTypeState } from './useFilterTypeState';
import styles from './openFilterUnit.module.scss';

const classNameBuilder = classnames.bind(styles);

const dropdownPlaceholder = (
    <FormattedMessage id="app.campaigns.reports.filters.placeholder" defaultMessage="Select from..." />
);
const textStyle = {
    color: styles['dropdown-color'],
    fontSize: styles['dropdown-fontSize'],
};
const CustomRangePickerHeader = props => (
    <RangePickerHeader {...props} className={classNameBuilder('filter-date-range-container')} />
);
const commonDropdownStyles = ({ omitMinWidth } = {}) => ({
    menu: provided => ({
        ...provided,
        minWidth: pxToRem(200),
    }),
    container: provided => ({
        ...provided,
        width: 'max-content',
        ...(omitMinWidth ? {} : { minWidth: pxToRem(135) }),
    }),
    control: provided => ({
        ...provided,
        border: 'none',
        borderRadius: 0,
        minHeight: 0,
        padding: styles['dropdown-padding'],
        backgroundColor: 'unset',
        boxShadow: 'none',
        '&:hover': {
            border: 0,
            boxShadow: 'none',
        },
        flexGrow: 1,
    }),
    placeholder: () => ({ ...textStyle, position: 'absolute' }),
    singleValue: () => ({
        ...textStyle,
        width: 'max-content',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
    input: () => ({ margin: 0 }),
});

const asyncDropdownStyles = {
    ...commonDropdownStyles(),
    singleValue: () => ({
        ...commonDropdownStyles().singleValue(),
        position: 'absolute',
    }),
};

export const OpenFilterUnit = ({
    initialFilter,
    types,
    disabledTypes,
    getValueOptions,
    onCancel,
    onApply,
    isMultiSelectFilter,
    getApplyBtnMetricsAttrs,
    availableFiltersMap,
}) => {
    const { type, setType, filterTypeOptions } = useFilterTypeState({
        initialFilter,
        types,
        disabledTypes,
        availableFiltersMap,
    });
    const { operator, operatorLabel, setOperator, operatorOptions, operatorRef } = useFilterOperatorState({
        initialFilter,
        type,
        availableFiltersMap,
    });
    const {
        showInput,
        value: plainValue,
        valueType: plainValueType,
        setValue: setPlainValue,
        placeholder: plainValuePlaceholder,
        showAdditionalInput,
        additionalValue: additionalPlainValue,
        setAdditionalValue: setAdditionalPlainValue,
        additionalPlaceholder: additionalPlainValuePlaceholder,
        cleanValues: cleanPlainValues,
        isComplete: isPlainValuesComplete,
        shouldShowRangePicker,
    } = useFilterPlainValuesState({ initialFilter, operator, type, availableFiltersMap });
    const {
        showValuesDropdown,
        values,
        setValues,
        setValuePage,
        isComplete: isListValuesComplete,
        ...filterListValueProps
    } = useFilterListValueState({
        type,
        initialFilter,
        isMultiSelectFilter,
        getValueOptions,
        operator,
        operatorOptions,
        availableFiltersMap,
    });

    const allValuesCleaner = () => {
        setValues([]);
        setValuePage(0);
        cleanPlainValues();
    };
    const handleTypeChange = ({ value: newType }) => {
        if (newType === type) {
            return;
        }
        setType(newType);
        setOperator();
        allValuesCleaner();
    };
    const handleOperatorChange = ({ value: newOperator }) => {
        if (newOperator === operator) {
            return;
        }
        setOperator(newOperator);
    };
    const defaultDatePreset = PRESETS.SEVEN_DAYS;
    const [{ startDate, endDate, datePreset }, setDateRange, rangePickerPresetConfigs] = useDateRange();
    const isFilterComplete =
        type && (operator || isEmpty(operatorOptions)) && isPlainValuesComplete && isListValuesComplete;
    const applyButtonClassName = classNameBuilder('apply-button', { disabled: !isFilterComplete });
    const applyBtnMetricsAttrs = useMemo(
        () => getApplyBtnMetricsAttrs({ type, values, operator }),
        [getApplyBtnMetricsAttrs, type, values, operator]
    );
    const applyFilter = () => {
        const formatter = availableFiltersMap[type]?.formatter || identity;
        const resolvedValues = showValuesDropdown
            ? values
            : [plainValue, additionalPlainValue].filter(value => value !== '').map(formatter);
        const resolvedOperator = operator ? { value: operator, label: operatorLabel } : undefined;
        onApply({ type, values: resolvedValues, operator: resolvedOperator });
    };
    const handleRangeConfirmed = (startDate, endDate, preset) => {
        setDateRange({ [START_DATE]: startDate, [END_DATE]: endDate, [DATE_PRESET]: preset });
        setPlainValue(startDate);
        setAdditionalPlainValue(endDate);
    };
    return (
        <div className={styles['container']}>
            <div className={styles['dropdowns-container']}>
                <DelimiterWrapper>
                    <DropdownMenu
                        aria-label="Select filter type"
                        styles={commonDropdownStyles({ omitMinWidth: !!type })}
                        placeholder={dropdownPlaceholder}
                        autoFocus={!type}
                        openMenuOnFocus
                        selectedValueObject={{ value: type }}
                        options={filterTypeOptions}
                        onChange={handleTypeChange}
                    />
                    {!isEmpty(operatorOptions) && (
                        <DropdownMenu
                            kye={type}
                            ref={operatorRef}
                            openMenuOnFocus
                            aria-label="Select filter operator"
                            styles={commonDropdownStyles()}
                            placeholder={dropdownPlaceholder}
                            selectedValueObject={{ value: operator }}
                            options={operatorOptions}
                            onChange={handleOperatorChange}
                            disabled={!type}
                            className={styles['operator-dropdown']}
                        />
                    )}
                    {shouldShowRangePicker && (
                        <DashboardRangePicker
                            defaultPreset={null}
                            preset={!!startDate ? datePreset : defaultDatePreset}
                            className={styles['filter-range-picker']}
                            startDate={startDate || moment().subtract(defaultDatePreset.days, 'days')}
                            endDate={endDate || moment()}
                            onRangeConfirmed={handleRangeConfirmed}
                            confirmButtonText="APPLY"
                            customTimeFramePicker={<TimeFrame />}
                            displayFormat="DD/MM/YYYY"
                            presetConfigs={rangePickerPresetConfigs}
                            renderWeekHeaderElement={renderWeekHeaderElement}
                            headerComponent={CustomRangePickerHeader}
                            contentClassName={styles['filter-range-picker-content']}
                        />
                    )}
                    {!shouldShowRangePicker && (
                        <>
                            {showInput && (
                                <InputEditField
                                    value={plainValue}
                                    onChange={setPlainValue}
                                    inputClass={styles['input']}
                                    type={plainValueType}
                                    autoFocus={false}
                                    placeholder={plainValuePlaceholder}
                                />
                            )}
                            {showAdditionalInput && (
                                <InputEditField
                                    value={additionalPlainValue}
                                    onChange={setAdditionalPlainValue}
                                    inputClass={styles['input']}
                                    type={plainValueType}
                                    autoFocus={false}
                                    placeholder={additionalPlainValuePlaceholder}
                                />
                            )}
                        </>
                    )}
                    {showValuesDropdown && (
                        <AsyncDropdown
                            aria-label="Select filter value"
                            {...filterListValueProps}
                            key={type + operator}
                            className={styles['values-dropdown']}
                            styles={asyncDropdownStyles}
                            placeholder={dropdownPlaceholder}
                            disabled={!type}
                            backspaceRemovesValue={false}
                            manualMenuControl
                        />
                    )}
                    <StyledButton
                        aria-label={`Cancel ${type} filter editing`}
                        className={styles['remove-button']}
                        size={STYLED_BUTTON_SIZE.SMALL}
                        type={STYLED_BUTTON_TYPE.BORDERLESS_ICON}
                        iconBefore={<CloseIcon height={16} width={16} />}
                        onClick={onCancel}
                    />
                </DelimiterWrapper>
            </div>
            <StyledButton
                aria-label={`Apply${isFilterComplete ? '' : '-disabled'}`}
                disabled={!isFilterComplete}
                className={applyButtonClassName}
                size={STYLED_BUTTON_SIZE.MEDIUM}
                type={STYLED_BUTTON_TYPE.STRONG}
                onClick={applyFilter}
                {...applyBtnMetricsAttrs}
            >
                <FormattedMessage id="app.actionButtons.apply" defaultMessage="Apply" />
            </StyledButton>
        </div>
    );
};

OpenFilterUnit.propTypes = {
    /** Initial filter value */
    initialFilter: PropTypes.shape({
        type: PropTypes.string,
        values: PropTypes.array,
    }),
    /** array of available types to choose from */
    types: PropTypes.array,
    /** es6 set of disabled types, disables the option in type dropdown */
    disabledTypes: PropTypes.object,
    /** async function (type, searchText, page), returns {options: [{value, label}], hasMore} */
    getValueOptions: PropTypes.func,
    /** called when user clicks the x button  */
    onCancel: PropTypes.func,
    /** called when user clicks apply button */
    onApply: PropTypes.func,
    /** return value is spread into apply button for metrics */
    getApplyBtnMetricsAttrs: PropTypes.func,
};

OpenFilterUnit.defaultProps = {
    initialFilter: {},
    types: [],
    disabledTypes: new Set(),
    getValueOptions: () => ({ options: [], hasMore: false }),
    getApplyBtnMetricsAttrs: noop,
};
